import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, MinusCircle, RefreshCw, ExternalLink,
} from "lucide-react";
import api, { apiError } from "@/lib/api";

/**
 * Integrations Health — shows last 7-day success/error stats per external service.
 *
 * Two render modes:
 *   <IntegrationsHealth variant="compact" />  → for the Dashboard (single card, no error list)
 *   <IntegrationsHealth variant="full" />     → for Settings (full breakdown with recent errors)
 */

const SERVICES = [
  { id: "tavily", label: "Tavily", desc: "Web search / DNA", where: "https://app.tavily.com/" },
  { id: "apollo", label: "Apollo.io", desc: "Decision-makers", where: "https://app.apollo.io/" },
  { id: "hunter", label: "Hunter.io", desc: "Email enrichment", where: "https://hunter.io/" },
  { id: "llm", label: "Claude / LLM", desc: "Pitch & analysis", where: null },
];

const STATUS_META = {
  ok:   { icon: CheckCircle2,  color: "text-emerald-600",  bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-300 dark:border-emerald-900", label: "OK" },
  warn: { icon: AlertTriangle, color: "text-amber-600",    bg: "bg-amber-50 dark:bg-amber-950/30",     border: "border-amber-300 dark:border-amber-900",     label: "Dégradé" },
  down: { icon: XCircle,       color: "text-rose-600",     bg: "bg-rose-50 dark:bg-rose-950/30",       border: "border-rose-300 dark:border-rose-900",       label: "Indisponible" },
  idle: { icon: MinusCircle,   color: "text-muted-foreground", bg: "bg-muted/40",                        border: "border-border",                              label: "Inactif" },
};

function _fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `il y a ${Math.round(diff)} s`;
  if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
  return `il y a ${Math.round(diff / 86400)} j`;
}

export default function IntegrationsHealth({ variant = "full", days = 7 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/integrations/health?days=${days}`);
      setData(data);
    } catch (e) {
      console.error("[IntegrationsHealth]", apiError(e));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="card-soft" data-testid="integrations-health">
        <CardContent className="py-4 text-xs text-muted-foreground">Chargement…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const services = SERVICES.map((s) => ({ ...s, ...(data.services[s.id] || {}) }));

  // Compact card for Dashboard
  if (variant === "compact") {
    const downServices = services.filter((s) => s.status === "down");
    const warnServices = services.filter((s) => s.status === "warn");
    const okServices = services.filter((s) => s.status === "ok");
    const overall = downServices.length ? "down" : warnServices.length ? "warn" : okServices.length ? "ok" : "idle";
    const meta = STATUS_META[overall];
    const Icon = meta.icon;

    return (
      <Card className="card-soft" data-testid="integrations-health-compact">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            Santé des intégrations
          </CardTitle>
          <Badge variant="outline" className={`${meta.border} ${meta.color} text-[10px]`}>
            <Icon className="h-3 w-3 mr-1" /> {meta.label}
          </Badge>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-1.5">
            {services.map((s) => {
              const m = STATUS_META[s.status || "idle"];
              const SIcon = m.icon;
              return (
                <div
                  key={s.id}
                  className={`flex items-center justify-between text-[11px] px-2 py-1.5 rounded border ${m.border} ${m.bg}`}
                  data-testid={`health-compact-${s.id}`}
                >
                  <span className="font-medium truncate">{s.label}</span>
                  <SIcon className={`h-3.5 w-3.5 ${m.color} shrink-0`} />
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-muted-foreground text-center mt-2">
            Fenêtre : {data.window_days} j · Détails dans Settings → Intégrations
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full view for Settings
  return (
    <Card className="card-soft" data-testid="integrations-health">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          Santé des intégrations <span className="text-[10px] font-normal text-muted-foreground">(7 derniers jours)</span>
        </CardTitle>
        <Button
          size="icon" variant="ghost" className="h-6 w-6"
          onClick={load} disabled={refreshing}
          data-testid="health-refresh-btn"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {services.map((s) => {
          const m = STATUS_META[s.status || "idle"];
          const SIcon = m.icon;
          const total = (s.success_count || 0) + (s.error_count || 0);
          const ratePct = s.success_rate == null ? null : Math.round(s.success_rate * 100);
          return (
            <div
              key={s.id}
              className={`border ${m.border} ${m.bg} rounded-md p-2.5 space-y-1.5`}
              data-testid={`health-service-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <SIcon className={`h-4 w-4 ${m.color} shrink-0`} />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold flex items-center gap-1.5">
                      {s.label}
                      {s.where && (
                        <a href={s.where} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground hover:underline">
                          <ExternalLink className="h-2.5 w-2.5 inline" />
                        </a>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{s.desc}</div>
                  </div>
                </div>
                <Badge variant="outline" className={`${m.border} ${m.color} text-[10px] shrink-0`}>
                  {m.label}
                </Badge>
              </div>

              <div className="flex items-center justify-between text-[11px]">
                <div className="flex items-center gap-3">
                  <span className="text-emerald-700 dark:text-emerald-400">
                    ✓ {s.success_count || 0}
                  </span>
                  <span className="text-rose-700 dark:text-rose-400">
                    ✗ {s.error_count || 0}
                  </span>
                  {total > 0 && (
                    <span className="font-medium tabular-nums" data-testid={`health-rate-${s.id}`}>
                      {ratePct}%
                    </span>
                  )}
                  {total === 0 && (
                    <span className="text-muted-foreground italic text-[10px]">Aucun appel sur la fenêtre</span>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground">
                  Dernier succès : {_fmtRelative(s.last_success_at)}
                </span>
              </div>

              {(s.recent_errors || []).length > 0 && (
                <details className="text-[10px] pt-1 border-t border-border/60">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                    {s.recent_errors.length} erreur{s.recent_errors.length > 1 ? "s" : ""} récente{s.recent_errors.length > 1 ? "s" : ""}
                  </summary>
                  <div className="space-y-1 mt-1.5 pl-2">
                    {s.recent_errors.map((err, i) => (
                      <div key={i} className="text-rose-800/90 dark:text-rose-300/90" data-testid={`health-error-${s.id}-${i}`}>
                        <span className="font-semibold">{err.step}</span>
                        <span className="text-muted-foreground"> · {_fmtRelative(err.at)}</span>
                        <div className="text-rose-700/70 dark:text-rose-400/70 pl-2 truncate">{err.error}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
        <div className="text-[10px] text-muted-foreground pt-1">
          Statut : <b>OK</b> (≥ 95% succès) · <b>Dégradé</b> (60-95%) · <b>Indisponible</b> (&lt; 60%) · <b>Inactif</b> (aucun appel)
        </div>
      </CardContent>
    </Card>
  );
}
